import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS } from '@neke/core';
import type { ApiError } from '../api/client';
import { api } from '../api/client';
import { track } from '../analytics';

/**
 * Скачать отчёт файлом и — если система умеет — сразу отправить его.
 *
 * Доступ к разбору личный и внутри приложения не передаётся. Показать отчёт
 * своей паре человек всё равно вправе: он за него заплатил. PDF и есть этот
 * способ — дальше файл живёт в мессенджере, а доступ остаётся личным.
 */
export function ReportFile({ pairId, compact = false }: { pairId: string; compact?: boolean }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'download' | 'share' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Отправка файла есть не везде: в десктопных браузерах и части webview
  // navigator.share файлы не принимает — там остаётся только скачивание.
  const canShare =
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    typeof navigator.share === 'function';

  const load = async () => {
    const { blob, filename } = await api.reportPdf(pairId);
    return new File([blob], filename, { type: 'application/pdf' });
  };

  const download = () => {
    setBusy('download');
    setError(null);
    void load()
      .then((file) => {
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
        // Ссылку на объект держим до конца скачивания: Safari обрывает
        // загрузку, если освободить её сразу после click().
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
        void track(ANALYTICS_EVENTS.reportPdfDownloaded, { pair_id: pairId });
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setBusy(null));
  };

  const share = () => {
    setBusy('share');
    setError(null);
    void load()
      .then(async (file) => {
        if (!navigator.canShare({ files: [file] })) {
          setError(t('report.shareUnsupported'));
          return;
        }
        await navigator.share({ files: [file], title: t('report.shareTitle') });
        void track(ANALYTICS_EVENTS.reportPdfShared, { pair_id: pairId });
      })
      .catch((e: unknown) => {
        // Пользователь закрыл системное окно — это не ошибка, молчим.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : t('report.shareFailed'));
      })
      .finally(() => setBusy(null));
  };

  return (
    <>
      <button
        className={compact ? 'btn btn-ghost' : 'btn btn-gold'}
        disabled={busy !== null}
        onClick={download}
      >
        {busy === 'download' ? t('report.pdfPreparing') : t('report.pdfDownload')}
      </button>

      {canShare && (
        <button className="btn btn-ghost" disabled={busy !== null} onClick={share}>
          {busy === 'share' ? t('report.pdfPreparing') : t('report.pdfShare')}
        </button>
      )}

      <p className="fine">{error ?? t('report.pdfHint')}</p>
    </>
  );
}
